/**
 * A DANCER READS HER BAG GROUP (packet BG-10; DECISIONS 123 item 17, "Read
 * only"; DECISIONS 37 rule 2; DECISIONS 26 and 27).
 *
 * ONE PAGE, TWO DOORS. Her cookie dancer session opens it from the My bag
 * group door on DancerHome, with the view the dancer portal sent. Her Jr
 * Staff email account, or her promoted dancer login, opens it at /bag-groups
 * (BagGroupReadOnlyPage below), with the view netlify/functions/bagGroupView.mjs
 * sent. Both views come from one server resolver
 * (netlify/functions/lib/bagGroupView.mjs) and are read by one parser
 * (lib/bagGroupViewData.ts), so she sees the same thing on every sign-in.
 * Nothing on this page asks which sign-in she used.
 *
 * READ ONLY. No composer, no item buttons, no reaction buttons and no alert
 * settings: hearts and alerts wait for Bobbie's answer to plan question Q4,
 * and every bag write lane refuses her sign-ins anyway. The counts under a
 * post are shown, never who reacted.
 *
 * Times are studio time, America/Chicago (CLAUDE.md).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useUserDoc } from '../lib/users';
import { isDancerSelfSignIn } from '../lib/dancerSelfSignIn';
import { useSignInBagGroupView } from '../lib/bagGroupView';
import MessageAttachments from '../components/MessageAttachments';
import { BagGroupHowTo } from '../components/BagGroupHowTo';
import { LinkifiedText } from '../components/LinkifiedText';
import { STUDIO_TIME_ZONE } from '../lib/studioDay';
import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import {
  bagViewItemStatusLabel,
  bagViewMillis,
  bagViewRecentPosts,
  type BagGroupView,
  type BagGroupViewAttachment,
  type BagGroupViewGroup,
  type BagGroupViewMessage,
  type BagGroupViewPost,
} from '../lib/bagGroupViewData';

// Studio time, whatever zone the device is in (CLAUDE.md).

function studioTime(iso: string | null): string {
  const ms = bagViewMillis(iso);
  if (ms === null) return '';
  return new Date(ms).toLocaleString('en-US', {
    timeZone: STUDIO_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function dayLabel(key: string | null): string {
  if (!key) return '';
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const cardStyle: React.CSSProperties = {
  background: 'var(--off-white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 'var(--space-4)',
  color: 'var(--ink)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  minWidth: 0,
};

const eyebrowStyle: React.CSSProperties = {
  font: '800 12px Montserrat, Lexend, sans-serif',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--silver-deep)',
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  background: 'var(--lime-whisper)',
  color: 'var(--lime-deep)',
  border: '1px solid var(--studio-green)',
};

const quietButtonStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '0 var(--space-3)',
  borderRadius: 12,
  border: '1px solid var(--line)',
  background: 'var(--paper)',
  color: 'var(--ink)',
  font: 'inherit',
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'left',
};

/**
 * Files on a post, through the shared attachment component every dancer
 * board uses (its videos play through the one shared player, packet J20).
 */
function Attachments({ attachments }: { attachments: BagGroupViewAttachment[] }) {
  if (attachments.length === 0) return null;
  return <MessageAttachments attachments={attachments} compact />;
}

function Byline({ message }: { message: BagGroupViewMessage }) {
  const when = studioTime(message.createdAt);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-soft)' }}>
      <strong style={{ color: 'var(--ink)', fontSize: 14 }}>{message.authorName}</strong>
      {message.authorRole !== 'family' && <span style={chipStyle}>Studio</span>}
      {when && <span>{when}</span>}
    </div>
  );
}

function Counts({ post }: { post: BagGroupViewPost }) {
  const reactions = Object.entries(post.reactionCounts);
  if (reactions.length === 0 && post.ackCount === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 13, color: 'var(--ink-soft)' }} aria-label="Reactions on this post">
      {reactions.map(([emoji, n]) => (
        <span key={emoji} style={{ border: '1px solid var(--line)', borderRadius: 999, padding: '2px 9px' }}>
          {emoji} {n}
        </span>
      ))}
      {post.ackCount > 0 && <span>{post.ackCount} saw it</span>}
    </div>
  );
}

function PostCard({ post }: { post: BagGroupViewPost }) {
  const [open, setOpen] = useState(false);
  return (
    <article style={{ borderTop: '1px solid var(--line)', paddingTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <Byline message={post} />
      {post.text && <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}><LinkifiedText text={post.text} /></div>}
      <Attachments attachments={post.attachments} />
      <Counts post={post} />
      {post.replyCount > 0 && (
        <button type="button" style={quietButtonStyle} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide replies' : `Show ${post.replyCount} ${post.replyCount === 1 ? 'reply' : 'replies'}`}
        </button>
      )}
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 'var(--space-3)', borderLeft: '3px solid var(--lime-whisper)' }}>
          {post.replies.map((reply) => (
            <div key={reply.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <Byline message={reply} />
              {reply.text && <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}><LinkifiedText text={reply.text} /></div>}
              <Attachments attachments={reply.attachments} />
            </div>
          ))}
          {post.replies.length < post.replyCount && (
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Older replies are on the group page your parents open.</div>
          )}
        </div>
      )}
    </article>
  );
}

function GroupCard({ group }: { group: BagGroupViewGroup }) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const posts = useMemo(
    () => (term
      ? group.posts.filter((post) => [plainMessageText(post.text), post.authorName, ...post.replies.flatMap((r) => [plainMessageText(r.text), r.authorName])]
        .some((value) => value.toLowerCase().includes(term)))
      : group.posts),
    [group.posts, term],
  );
  return (
    <section style={cardStyle} aria-label={group.name} id={`bag-group-read-${group.id}`}>
      <div>
        {group.seasonName && <div style={eyebrowStyle}>{group.seasonName}</div>}
        <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, overflowWrap: 'anywhere' }}>{group.name}</h2>
        <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4 }}>
          {group.leadNames.length > 0
            ? `${group.leadNames.length === 1 ? 'Lead' : 'Leads'}: ${group.leadNames.join(' & ')}`
            : 'No lead set yet'}
        </div>
      </div>
      <div style={{ ...chipStyle, alignSelf: 'flex-start' }}>Reads only</div>
      <div style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
        You can read this group. Posts, sign-ups and drop-offs come from your parents and the studio.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={eyebrowStyle}>Your family is bringing</div>
        {group.items.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>Nothing on your family&apos;s list yet.</div>
        ) : (
          group.items.map((item) => (
            <div key={item.id} style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 15, overflowWrap: 'anywhere' }}>
                  {item.title}{item.quantity ? ` (${item.quantity})` : ''}
                </strong>
                <span style={chipStyle}>{bagViewItemStatusLabel(item.status)}</span>
              </div>
              {item.competitionName && (
                <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                  {item.competitionName}{item.competitionDate ? `, ${dayLabel(item.competitionDate)}` : ''}
                </div>
              )}
              {item.dropOffDate && (
                <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                  Drop off by {dayLabel(item.dropOffDate)}{item.dropOffLocation ? `, ${item.dropOffLocation}` : ''}
                </div>
              )}
              {item.note && <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.note}</div>}
            </div>
          ))
        )}
        {group.itemsTruncated && (
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }} data-bag-items-truncated>
            Showing the first {group.items.length} of your family&apos;s items. Your parents see the whole list.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={eyebrowStyle}>Posts</div>
        {group.posts.length > 6 && (
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search posts"
            aria-label={`Search posts in ${group.name}`}
            style={{ minHeight: 44, padding: '0 var(--space-3)', fontSize: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)' }}
          />
        )}
        {group.posts.length === 0 && <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>No posts yet.</div>}
        {group.posts.length > 0 && posts.length === 0 && <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>No post matches that.</div>}
        {posts.map((post) => <PostCard key={post.id} post={post} />)}
      </div>
    </section>
  );
}

/** The read-only page body, for either door. */
export function BagGroupReadOnly({
  view,
  loading = false,
  error = null,
  onRetry,
  focusGroupId,
}: {
  view: BagGroupView;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  focusGroupId?: string;
}) {
  const groups = useMemo(() => {
    if (!focusGroupId) return view.groups;
    const focused = view.groups.filter((group) => group.id === focusGroupId);
    return [...focused, ...view.groups.filter((group) => group.id !== focusGroupId)];
  }, [view.groups, focusGroupId]);
  if (loading) {
    return <div style={{ ...cardStyle, color: 'var(--ink-soft)' }} aria-busy="true">Finding your bag group...</div>;
  }
  if (error) {
    return (
      <div style={cardStyle} role="alert">
        <div>{error}</div>
        {onRetry && <button type="button" style={quietButtonStyle} onClick={onRetry}>Try again</button>}
      </div>
    );
  }
  if (groups.length === 0 && !view.scanTruncated) {
    return (
      <div style={cardStyle}>
        <strong style={{ fontSize: 18 }}>You are not in a bag group yet</strong>
        <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>When the studio puts you in one, it shows here.</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
      {/* How to use this page, her own three steps, on both doors (bag group
          rethink, 2026-10-19). Closed until she opens it. */}
      {groups.length > 0 && <BagGroupHowTo variant="dancer" />}
      {groups.map((group) => <GroupCard key={group.id} group={group} />)}
      {view.capped && (
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>You are in more bag groups than fit here. Ask the studio which ones.</div>
      )}
      {view.scanTruncated && (
        <div style={{ ...cardStyle, fontSize: 14 }} role="status" data-bag-scan-truncated>
          Your family is in a lot of groups, so a bag group of yours may be missing here. Ask the studio which bag group you are in.
        </div>
      )}
    </div>
  );
}

/**
 * HER BAG POSTS ON HER HOME (DECISIONS 37 rule 2: a dancer sees every post on
 * every wall board she belongs to, on her Home too). The newest few, each
 * naming its group, with one way into the full page. Renders nothing for a
 * dancer in no group, so a home never grows an empty card.
 */
export function BagGroupHomeCard({
  view,
  onOpen,
  limit = 3,
}: {
  view: BagGroupView;
  onOpen: () => void;
  limit?: number;
}) {
  if (view.groups.length === 0) return null;
  const recent = bagViewRecentPosts(view).slice(0, limit);
  const names = view.groups.map((group) => group.name).join(' & ');
  return (
    <section style={cardStyle} aria-label="From your bag group" id="dancer-bag-group-home">
      <div>
        <div style={eyebrowStyle}>From your bag group</div>
        <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, overflowWrap: 'anywhere' }}>{names}</div>
      </div>
      {recent.length === 0 ? (
        <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>No posts yet.</div>
      ) : (
        recent.map(({ group, post }) => (
          <div key={`${group.id}-${post.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 4, borderTop: '1px solid var(--line)', paddingTop: 'var(--space-2)', minWidth: 0 }}>
            <Byline message={post} />
            {view.groups.length > 1 && <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{group.name}</div>}
            <div style={{ fontSize: 14, lineHeight: 1.45, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {plainMessageText(post.text) || (post.attachments.length > 0 ? 'Shared a file.' : '')}
            </div>
          </div>
        ))
      )}
      <button type="button" style={{ ...quietButtonStyle, fontWeight: 800 }} onClick={onOpen}>
        Open my bag group &rsaquo;
      </button>
    </section>
  );
}

/**
 * The same home card on a Jr Staff's email home (Landing.tsx), fed by the
 * Firebase door, so her bag posts reach her Home on that sign-in exactly as
 * they do on her dancer sign-in. Asks nothing, and draws nothing, for any
 * sign-in that is not the dancer herself.
 */
export function SignInBagGroupHomeCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userDoc } = useUserDoc();
  const enabled = Boolean(user && isDancerSelfSignIn(userDoc));
  const { view } = useSignInBagGroupView(user?.uid, enabled);
  if (!enabled) return null;
  return <BagGroupHomeCard view={view} onOpen={() => navigate('/bag-groups')} />;
}

/**
 * The Firebase door's page: /bag-groups and /bag-groups/:id for a sign-in
 * isDancerSelfSignIn admits. BagGroups.tsx and BagGroupDetail.tsx render
 * this in place of the member pages, so the email account no longer lands on
 * "The studio team will add you when groups are ready."
 */
export default function BagGroupReadOnlyPage({ focusGroupId }: { focusGroupId?: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userDoc } = useUserDoc();
  const enabled = Boolean(user && isDancerSelfSignIn(userDoc));
  const { view, loading, error, reload } = useSignInBagGroupView(user?.uid, enabled);
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', color: 'var(--ink)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'var(--space-4) 16px var(--space-6, 48px)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={eyebrowStyle}>Comp team</div>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 30, lineHeight: 1.1 }}>My bag group</h1>
        </header>
        <button type="button" style={quietButtonStyle} onClick={() => navigate('/')}>&#8592; Back home</button>
        <BagGroupReadOnly view={view} loading={loading} error={error} onRetry={reload} focusGroupId={focusGroupId} />
      </div>
    </div>
  );
}
